/**
 * Typed per-type parameter schema for sketch objects.
 *
 * Each sketch-object type declares its unique parameters as a {@link ParamSchema}.
 * The schema is the single source of truth for:
 *  - property registration ({@link registerParamProperties}) — every param
 *    becomes a reactive property `sketchobject_<typeId>_<key>`, scoped to the
 *    owning type in the property panel via a visibility condition on
 *    `sketchobject_type_id`;
 *  - typed reads ({@link readParamValues}) — parametric glyph builders receive
 *    a fully typed {@link ParamValues} record;
 *  - creation defaults ({@link paramInitialProperties}).
 *
 * Param property identifiers are SCHEMA: once a type has shipped, its id and
 * param keys must never be renamed (stored project data references them).
 */

import { AnnotationType, PropertyType, PropertyValueType, propertyRegistry, getGlobalPropertyService, type ReactiveProperty } from '@pointorama/potree-shared';

interface BaseParamSpec {
  label: string;
  /** Panel row order override. Params default to sequential slots from 20
   *  (below the shared width/depth/inset block at 10–12); set a value below
   *  12 to lift a param above "distance to wall" (e.g. the stair's step). */
  order?: number;
}

export interface FloatParamSpec extends BaseParamSpec {
  kind: 'float';
  default: number;
  min?: number;
  max?: number;
  decimals?: number;
  /** When true the panel renders the value with the user's meter/feet preference. */
  distance?: boolean;
}

export interface IntParamSpec extends BaseParamSpec {
  kind: 'int';
  default: number;
  min?: number;
  max?: number;
}

export interface BoolParamSpec extends BaseParamSpec {
  kind: 'boolean';
  default: boolean;
}

export interface SelectParamSpec<V extends string = string> extends BaseParamSpec {
  kind: 'select';
  default: V;
  options: ReadonlyArray<{ value: V; label: string }>;
}

export type ParamSpec = FloatParamSpec | IntParamSpec | BoolParamSpec | SelectParamSpec;

export type ParamSchema = Record<string, ParamSpec>;

type ParamValue<S extends ParamSpec> = S extends FloatParamSpec | IntParamSpec ? number : S extends BoolParamSpec ? boolean : S extends SelectParamSpec<infer V> ? V : never;

/** The typed value record a schema produces, e.g. `{ doorCount: number; doorsOpen: boolean }`. */
export type ParamValues<S extends ParamSchema> = { [K in keyof S]: ParamValue<S[K]> };

/** Neutral initial-property shape; mapped to the creation pipeline's
 *  InitialProperty by the annotation factory (keeps potree/ free of hook imports). */
export interface SketchObjectInitialParam {
  identifier: string;
  value: number | string | boolean;
  valueType: PropertyValueType;
}

/** ConvertType.DISTANCE — unit suffix + meter/feet conversion on distance floats. */
const CONVERT_TYPE_DISTANCE = 1;

/**
 * Where a type's param properties live: sketch-object types on SKETCH_OBJECT
 * (`sketchobject_<typeId>_<key>`), organisation doors / windows on
 * OPENING_SKETCH (`openingsketch_<typeId>_<key>`). SCHEMA — never rename.
 */
export interface ParamPropertyScope {
  prefix: string;
  annotationType: AnnotationType;
}

export const SKETCH_OBJECT_PARAM_SCOPE: ParamPropertyScope = { prefix: 'sketchobject', annotationType: AnnotationType.SKETCH_OBJECT };
export const OPENING_PARAM_SCOPE: ParamPropertyScope = { prefix: 'openingsketch', annotationType: AnnotationType.OPENING_SKETCH };

/** Panel visibility condition of a param row (shown while the object is of its type). */
export interface ParamVisibilityCondition {
  propertyIdentifier: string;
  value: string;
}

export function paramPropertyIdentifier(typeId: string, key: string, scope: ParamPropertyScope = SKETCH_OBJECT_PARAM_SCOPE): string {
  return `${scope.prefix}_${typeId}_${key}`;
}

function paramValueType(spec: ParamSpec): PropertyValueType {
  switch (spec.kind) {
    case 'float':
      return PropertyValueType.FLOAT;
    case 'int':
      return PropertyValueType.INT;
    case 'boolean':
      return PropertyValueType.BOOLEAN;
    case 'select':
      return PropertyValueType.SELECT;
  }
}

/**
 * Register every param of `schema` as a PROJECT_GLOBAL reactive property on
 * SKETCH_OBJECT, visible in the panel only while the selected object's
 * `sketchobject_type_id` equals `typeId`.
 */
export function registerParamProperties(
  typeId: string,
  schema: ParamSchema,
  scope: ParamPropertyScope = SKETCH_OBJECT_PARAM_SCOPE,
  visibility: readonly ParamVisibilityCondition[] = [{ propertyIdentifier: 'sketchobject_type_id', value: typeId }],
): void {
  let order = 20; // after the shared width/depth/inset block (10-12)
  for (const [key, spec] of Object.entries(schema)) {
    const identifier = paramPropertyIdentifier(typeId, key, scope);
    if (propertyRegistry.isRegistered(identifier)) continue;
    propertyRegistry.register({
      type: PropertyType.PROJECT_GLOBAL,
      identifier,
      name: spec.label,
      valueType: paramValueType(spec),
      defaultValue: spec.default,
      annotationTypes: [scope.annotationType],
      // The label doubles as the tooltip. Never compose a sentence here — a
      // generated string can't be extracted for translation, so it would
      // always surface in English (PK ticket: untranslated prop tooltips).
      description: spec.label,
      validation:
        spec.kind === 'select'
          ? { options: spec.options.map((o) => ({ value: o.value, label: o.label })) }
          : spec.kind === 'float' || spec.kind === 'int'
            ? { min: spec.min, max: spec.max }
            : undefined,
      ui: {
        group: 'information',
        order: spec.order ?? order++,
        hiddenInGroup: true,
        decimals: spec.kind === 'float' ? spec.decimals : undefined,
        useUserPreferenceConvertType: spec.kind === 'float' && spec.distance ? CONVERT_TYPE_DISTANCE : undefined,
        visibilityConditions: [...visibility],
      },
    });
  }
}

/** Inverse of {@link registerParamProperties} (runtime custom types only). */
export function unregisterParamProperties(typeId: string, schema: ParamSchema, scope: ParamPropertyScope = SKETCH_OBJECT_PARAM_SCOPE): void {
  for (const key of Object.keys(schema)) {
    const identifier = paramPropertyIdentifier(typeId, key, scope);
    if (propertyRegistry.isRegistered(identifier)) propertyRegistry.unregister(identifier);
  }
}

/** Reactive property of one NUMERIC param of a placed object — typed write
 *  access for coupled-param sync (see SketchObjectTypeDefinition.syncParams). */
export function getNumberParamProperty(typeId: string, key: string, objectId: string): ReactiveProperty<number> {
  const service = getGlobalPropertyService();
  return service.getProperty(paramPropertyIdentifier(typeId, key), objectId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<number>;
}

/** Read all params of `schema` for one placed object as a typed record. */
export function readParamValues<S extends ParamSchema>(typeId: string, schema: S, objectId: string, scope: ParamPropertyScope = SKETCH_OBJECT_PARAM_SCOPE): ParamValues<S> {
  const service = getGlobalPropertyService();
  return coerceParamValues(schema, (key) => service.getProperty(paramPropertyIdentifier(typeId, key, scope), objectId, PropertyType.PROJECT_GLOBAL).value);
}

/** The reactive param properties of one object (change subscriptions). */
export function paramProperties(typeId: string, schema: ParamSchema, objectId: string, scope: ParamPropertyScope = SKETCH_OBJECT_PARAM_SCOPE): ReactiveProperty<unknown>[] {
  const service = getGlobalPropertyService();
  return Object.keys(schema).map((key) => service.getProperty(paramPropertyIdentifier(typeId, key, scope), objectId, PropertyType.PROJECT_GLOBAL) as ReactiveProperty<unknown>);
}

/** `raw` values as a typed record of `schema` (missing or mistyped entries → defaults). */
export function paramValuesFrom<S extends ParamSchema>(schema: S, raw: Readonly<Record<string, unknown>>): ParamValues<S> {
  return coerceParamValues(schema, (key) => raw[key]);
}

function coerceParamValues<S extends ParamSchema>(schema: S, get: (key: string) => unknown): ParamValues<S> {
  const values: Record<string, number | string | boolean> = {};
  for (const [key, spec] of Object.entries(schema)) {
    const raw = get(key);
    switch (spec.kind) {
      case 'float':
      case 'int':
        values[key] = typeof raw === 'number' ? raw : spec.default;
        break;
      case 'boolean':
        values[key] = typeof raw === 'boolean' ? raw : spec.default;
        break;
      case 'select':
        values[key] = typeof raw === 'string' && raw !== '' ? raw : spec.default;
        break;
    }
  }
  // Assembled per-key with the matching runtime type above; the record shape
  // is exactly ParamValues<S> (same class of assertion as the property getters).
  return values as ParamValues<S>;
}

/** Default values of `schema` as initial properties for annotation creation. */
export function paramInitialProperties(typeId: string, schema: ParamSchema): SketchObjectInitialParam[] {
  return Object.entries(schema).map(([key, spec]) => ({
    identifier: paramPropertyIdentifier(typeId, key),
    value: spec.default,
    valueType: paramValueType(spec),
  }));
}

/** Default values of `schema` as a typed record (ghost previews, thumbnails). */
export function defaultParamValues<S extends ParamSchema>(schema: S): ParamValues<S> {
  const values: Record<string, number | string | boolean> = {};
  for (const [key, spec] of Object.entries(schema)) {
    values[key] = spec.default;
  }
  return values as ParamValues<S>;
}
